import Big from 'big.js';
import { useMemo } from 'react';

import { defineMessages } from 'react-intl';
import { useTheme, type DefaultTheme } from 'styled-components';
import { getIntlKey } from '../../contexts';
import { useCountdownTime } from '../../hooks/useCountdownTime';
import { useIntl } from '../../hooks/useIntl';
import { isOrderPendingFix, type Order } from '../../types/Order';
import { DecisionStatusEnum, OrdStatusEnum, type PricingModeEnum } from '../../types/types';
import { formattedOrderTime, isDateInThePast, isKeyIn, isOrderPaused } from '../../utils';
import { includesEnumValue } from '../../utils/enumlikeHandlers';
import { isOrderComplete } from '../../utils/isOrderComplete';
import { orderStatusTextToColor } from '../../utils/order';
import { Box } from '../Core';
import { Icon, IconName, type IconProps } from '../Icons';
import { IndicatorDotVariants, IndicatorDotWrapper } from '../IndicatorDot';
import { Text } from '../Text';
import { Tooltip } from '../Tooltip';
import { Wrapper } from './styles';
import { OrderStatusSize, OrderStatusText } from './types';

const messages = defineMessages({
  open: {
    defaultMessage: 'Open',
    id: 'Status.open',
  },
  filled: {
    defaultMessage: 'Filled',
    id: 'Status.filled',
  },
  rejected: {
    defaultMessage: 'Rejected',
    id: 'Status.rejected',
  },
  systemPaused: {
    defaultMessage: 'System Paused',
    id: 'Status.systemPaused',
  },
  paused: {
    defaultMessage: 'Paused',
    id: 'Status.paused',
  },
  staged: {
    defaultMessage: 'Staged',
    id: 'Status.staged',
  },
  waiting: {
    defaultMessage: 'Waiting',
    id: 'Status.waiting',
  },
  filledAndCanceled: {
    defaultMessage: 'Filled & Canceled',
    id: 'Status.filledAndCanceled',
  },
  canceled: {
    defaultMessage: 'Canceled',
    id: 'Status.canceled',
  },
  pendingCancel: {
    defaultMessage: 'Pending Cancel',
    id: 'Status.pendingCancel',
  },
  pendingReplace: {
    defaultMessage: 'Pending Replace',
    id: 'Status.pendingReplace',
  },
  pendingFix: {
    defaultMessage: 'Pending Fix',
    id: 'Status.pendingFix',
  },
  allCanceled: {
    defaultMessage: 'All Canceled',
    id: 'Status.allCanceled',
  },
  allFilled: {
    defaultMessage: 'All Filled',
    id: 'Status.allFilled',
  },
  allOpen: {
    defaultMessage: 'All Open',
    id: 'Status.allOpen',
  },
  allRejected: {
    defaultMessage: 'All Rejected',
    id: 'Status.allRejected',
  },
  doneForDay: {
    defaultMessage: 'Done For Day',
    id: 'Status.doneForDay',
  },
  holding: {
    defaultMessage: 'Holding',
    id: 'Status.holding',
  },
  new: {
    defaultMessage: 'New',
    id: 'Status.new',
  },
  partiallyFilled: {
    defaultMessage: 'Partially Filled',
    id: 'Status.partiallyFilled',
  },
  pendingNew: {
    defaultMessage: 'Pending New',
    id: 'Status.pendingNew',
  },
  replaced: {
    defaultMessage: 'Replaced',
    id: 'Status.replaced',
  },
  unknown: {
    defaultMessage: 'Unknown',
    id: 'Status.unknown',
  },
});
export const orderStatusMessages = messages;

const orderStatusTextMapping = {
  [OrdStatusEnum.Filled]: OrderStatusText.Filled,
  [OrdStatusEnum.PendingCancel]: OrderStatusText.PendingCancel,
  [OrdStatusEnum.PendingReplace]: OrderStatusText.PendingReplace,
  [OrdStatusEnum.Rejected]: OrderStatusText.Rejected,
  [OrdStatusEnum.New]: OrderStatusText.Open,
  [OrdStatusEnum.PartiallyFilled]: OrderStatusText.Open,
  [OrdStatusEnum.PendingNew]: OrderStatusText.Open,
  [OrdStatusEnum.Holding]: OrderStatusText.Open,
  [OrdStatusEnum.Replaced]: OrderStatusText.Open,
  [OrdStatusEnum.PendingFix]: OrderStatusText.PendingFix,
} satisfies Partial<Record<OrdStatusEnum, string>>;

export type OrderForStatus = Pick<Order, 'OrdStatus' | 'OrderQty' | 'CumQty' | 'DecisionStatus' | 'PricingMode'>;
export type OrderLike = Partial<OrderForStatus>;

export type GetOrderStatusTextParams =
  | {
      /** Pass in a full {@link Order} object */
      type: 'order';
      order: OrderForStatus;
      isCustomerOrder: boolean;
    }
  | {
      /** Pass in an order-like object, with expected fallbacks */
      type: 'orderLike';
      order: OrderLike;
      isCustomerOrder: boolean;
    };

export const getOrderStatusText = ({ type, order, isCustomerOrder }: GetOrderStatusTextParams): OrderStatusText => {
  switch (type) {
    case 'order':
      return getOrderStatusTextInner({
        ordStatus: order.OrdStatus,
        orderQty: order.OrderQty ?? '0',
        cumQty: order.CumQty ?? '0',
        decisionStatus: order.DecisionStatus,
        pricingMode: order.PricingMode,
        isCustomerOrder,
      });
    case 'orderLike':
      return getOrderStatusTextInner({
        ordStatus: order.OrdStatus ?? OrdStatusEnum.PendingNew,
        orderQty: order.OrderQty ?? '0',
        cumQty: order.CumQty ?? '0',
        decisionStatus: order.DecisionStatus ?? DecisionStatusEnum.Active,
        pricingMode: order.PricingMode,
        isCustomerOrder,
      });
  }
};

const getOrderStatusTextInner = ({
  ordStatus,
  orderQty,
  cumQty,
  decisionStatus,
  pricingMode,
  isCustomerOrder,
}: {
  ordStatus: Order['OrdStatus'];
  orderQty: Order['OrderQty'];
  cumQty: Order['CumQty'];
  decisionStatus?: Order['DecisionStatus'];
  pricingMode?: Order['PricingMode'];
  isCustomerOrder: boolean;
}): OrderStatusText => {
  if (!isOrderComplete(ordStatus)) {
    if (decisionStatus === DecisionStatusEnum.Paused) {
      return OrderStatusText.Paused;
    }
    if (decisionStatus === DecisionStatusEnum.SystemPaused) {
      return OrderStatusText.SystemPaused;
    }
    if (decisionStatus === DecisionStatusEnum.Staged) {
      return OrderStatusText.Staged;
    }
    const WAITING_DECISION_STATUSES = [
      DecisionStatusEnum.WaitingForTrigger,
      DecisionStatusEnum.WaitingForStartTime,
    ] as const;
    if (decisionStatus && includesEnumValue(WAITING_DECISION_STATUSES, decisionStatus)) {
      return OrderStatusText.Waiting;
    }
  }

  if (!isCustomerOrder && isOrderPendingFix({ PricingMode: pricingMode, OrdStatus: ordStatus })) {
    return OrderStatusText.PendingFix;
  }

  if (ordStatus === OrdStatusEnum.Canceled) {
    return Big(cumQty || 0).gt(0) ? OrderStatusText.FilledAndCanceled : OrderStatusText.Canceled;
  }

  if (ordStatus === OrdStatusEnum.DoneForDay) {
    if (Big(cumQty || 0).eq(0)) {
      return OrderStatusText.Canceled;
    }
    // Synthetic ccy offers sometime fill over
    return Big(cumQty || 0).gte(orderQty) ? OrderStatusText.Filled : OrderStatusText.FilledAndCanceled;
  }

  const orderStatusText = orderStatusTextMapping[ordStatus];
  if (orderStatusText == null) {
    return OrderStatusText.Unknown;
  }

  return orderStatusText;
};

const StatusIcon = ({
  orderStatusText,
  ...props
}: { orderStatusText: OrderStatusText; theme?: DefaultTheme } & Omit<IconProps, 'color' | 'icon'>) => {
  const defaultTheme = useTheme();
  const theme = props.theme ?? defaultTheme;
  const color = orderStatusTextToColor(orderStatusText, useTheme() ?? theme);
  switch (orderStatusText) {
    case OrderStatusText.Filled:
    case OrderStatusText.FilledAndCanceled:
      return <Icon icon={IconName.CheckCircleSolid} color={color} {...props} />;
    case OrderStatusText.Canceled:
      return <Icon icon={IconName.CloseCircleSolid} color={color} {...props} />;
    case OrderStatusText.Rejected:
      return <Icon icon={IconName.ExclamationCircleSolid} color={color} {...props} />;
    case OrderStatusText.Paused:
    case OrderStatusText.SystemPaused:
      return <Icon icon={IconName.PauseCircle} color={color} {...props} />;
    case OrderStatusText.Staged:
      return <Icon icon={IconName.CircleDotted} color={color} {...props} />;
    case OrderStatusText.PendingFix:
      return <Icon icon={IconName.CheckCircleSolid} color={color} {...props} />;
    case OrderStatusText.PendingCancel:
    case OrderStatusText.PendingReplace:
    case OrderStatusText.Waiting:
      return <Icon icon={IconName.Clock} color={color} {...props} />;
    case OrderStatusText.Unknown:
      return <></>;
    default:
      return <Icon icon={IconName.Circle} color={color} {...props} />;
  }
};

type OrderStatusProps = {
  ordStatus: OrdStatusEnum;
  decisionStatus?: DecisionStatusEnum;
  cumQty?: string;
  orderQty?: string;
  align?: 'right' | 'left';
  iconPlacement?: 'right' | 'left';
  tooltipText?: string;
  theme?: DefaultTheme;
  title?: string;
  startTime?: string;
  endTime?: string;
  size?: OrderStatusSize;
  pricingMode?: PricingModeEnum;
  isCustomerOrder: boolean;
};

const StatusText = ({
  useStartTime,
  formattedCountdown,
  orderStatusText,
  paddingSize,
}: {
  useStartTime: boolean;
  formattedCountdown: string | undefined;
  orderStatusText: OrderStatusText;
  paddingSize: number;
}) => {
  const { formatMessage } = useIntl();
  const intlKey = getIntlKey(orderStatusText);
  let orderStatusTextTranslated: string = orderStatusText;
  if (isKeyIn(intlKey, messages)) {
    orderStatusTextTranslated = formatMessage(messages[intlKey]);
  }

  const isUnknown = orderStatusText === OrderStatusText.Unknown;

  if (formattedCountdown) {
    return <>{`${useStartTime ? 'Starts' : 'Ends'} in ${formattedCountdown}`}</>;
  }
  return (
    <>
      {isUnknown ? (
        <Text pl={paddingSize} fontStyle="italic">
          {orderStatusTextTranslated}
        </Text>
      ) : (
        orderStatusTextTranslated
      )}
    </>
  );
};

export function OrderStatus({
  ordStatus,
  decisionStatus,
  pricingMode,
  cumQty = '0',
  orderQty = '0',
  align = 'right',
  tooltipText,
  startTime,
  endTime,
  size = OrderStatusSize.Default,
  isCustomerOrder,
  ...props
}: OrderStatusProps) {
  const orderStatusText: OrderStatusText = getOrderStatusTextInner({
    ordStatus,
    decisionStatus,
    cumQty,
    orderQty,
    pricingMode,
    isCustomerOrder,
  });
  let variant: IndicatorDotVariants = IndicatorDotVariants.Warning;
  if (tooltipText?.includes('Order has unhedged size on legs')) {
    variant = IndicatorDotVariants.Negative;
  }
  const defaultTheme = useTheme();
  const theme = props.theme ?? defaultTheme;

  const isPaused: boolean = decisionStatus != null && isOrderPaused(decisionStatus);
  const useStartTime: boolean = startTime != null && !isDateInThePast(startTime);
  const completionTime = useMemo(
    () => Sugar.Date.create(useStartTime ? startTime : endTime),
    [useStartTime, startTime, endTime]
  );
  const orderCountdown = useCountdownTime(completionTime, ordStatus, 1000);
  const formattedCountdown: string | undefined =
    !isOrderComplete(ordStatus) && !isPaused && ((startTime != null && !isDateInThePast(startTime)) || endTime != null)
      ? formattedOrderTime(orderCountdown)
      : undefined;

  const tooltipContent = !!tooltipText && <Box maxWidth="300px">{tooltipText}</Box>;

  return tooltipContent ? (
    <Tooltip tooltip={tooltipContent} usePortal>
      {renderOrderStatus()}
    </Tooltip>
  ) : (
    renderOrderStatus()
  );

  function renderOrderStatus() {
    const iconSize = size === OrderStatusSize.Default ? 'fontSizeLarge' : 'fontSizeSmall';
    return (
      <Wrapper iconPlacement="left" align={align} theme={theme} size={size} {...props} data-testid="order-status">
        <StatusText
          useStartTime={useStartTime}
          formattedCountdown={formattedCountdown}
          orderStatusText={orderStatusText}
          //left padding for Unknown status based on icon size
          paddingSize={theme[iconSize] * theme.baseSize}
        />
        <IndicatorDotWrapper show={!!tooltipText} theme={theme} variant={variant}>
          <StatusIcon orderStatusText={orderStatusText} theme={theme} size={iconSize} />
        </IndicatorDotWrapper>
      </Wrapper>
    );
  }
}
